import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@app/components/Layout';
import { BookingFlow } from '@features/booking/components/BookingFlow';
import { AdminDashboard } from '@features/admin/components/AdminDashboard';
import { CompanyDashboard } from '@features/company-dashboard/components/CompanyDashboard';
import { TechnicianView } from '@features/technician/components/TechnicianView';
import { CustomerPortal } from '@features/customer-portal/components/CustomerPortal';
import { LandingPage } from '@features/home/components/LandingPage';
import { LoginModal } from '@features/auth/components/LoginModal';
import { VerificationPage } from '@features/verification/components/VerificationPage';
import { AlertModalHost, showAlertModal } from '@shared/components/common/AlertModal';
import { ToastHost } from '@shared/components/common/Toast';
import { api } from './services/api';
import { supabase } from './lib/supabase';
import { Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';
import {
  Appointment,
  Service,
  Venue,
  AddOn,
  User,
  Company,
  Customer,
  UserGroup,
  SystemConfig,
  MedicalCertificate,
  QuestionnaireTemplate,
  QuestionnaireSubmission,
  Payment
} from './types';

export default function App() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireTemplate[]>([]);
  const [submissions, setSubmissions] = useState<QuestionnaireSubmission[]>([]);
  const [certificates, setCertificates] = useState<MedicalCertificate[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | undefined>(undefined);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const silentLoadCountRef = useRef(0);

  const [view, setView] = useState<'home' | 'booking'>('home');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginContext, setLoginContext] = useState<'staff' | 'company' | 'customer'>('staff');
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [isUpdatingRecovery, setIsUpdatingRecovery] = useState(false);
  const [showCustomerPasswordGate, setShowCustomerPasswordGate] = useState(false);
  const [customerPassword, setCustomerPassword] = useState('');
  const [customerConfirmPassword, setCustomerConfirmPassword] = useState('');
  const [customerPasswordError, setCustomerPasswordError] = useState('');
  const [isUpdatingCustomerPassword, setIsUpdatingCustomerPassword] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const isVerifyMode = urlParams.get('mode') === 'verify';
  const isResetMode = urlParams.get('mode') === 'reset';

  const openRecoveryModal = () => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hasRecoveryHash = hashParams.get('type') === 'recovery';
    const hasRecoveryCode = isResetMode && (urlParams.get('code') || urlParams.get('token'));
    const hasRecoveryTokens = isResetMode && hashParams.get('access_token');
    if (hasRecoveryHash || hasRecoveryCode || hasRecoveryTokens) {
      setShowRecoveryModal(true);
    }
  };

  const clearRecoveryUrl = () => {
    const url = new URL(window.location.href);
    if (url.hash) {
      url.hash = '';
    }
    if (url.searchParams.get('mode') === 'reset') {
      url.searchParams.delete('mode');
    }
    if (url.searchParams.get('code')) {
      url.searchParams.delete('code');
    }
    if (url.searchParams.get('token')) {
      url.searchParams.delete('token');
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const hydrateSession = async () => {
    try {
      const session = await api.getCurrentSession();
      if (!session) {
        setCurrentUser(null);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setCurrentSessionId(null);
        setShowCustomerPasswordGate(false);
        return;
      }
      setCurrentSessionId(session.sessionId || null);
      if (session.type === 'staff') {
        setCurrentUser(session.profile);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
      } else if (session.type === 'company') {
        setCurrentCompany(session.profile);
        setCurrentUser(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
      } else if (session.type === 'customer') {
        setCurrentCustomer(session.profile);
        setCurrentUser(null);
        setCurrentCompany(null);
        setShowCustomerPasswordGate(Boolean(session.profile?.mustChangePassword));
      }
    } catch (e) {
      console.error('Hydration Failure:', e);
    }
  };

  const loadPublicBootstrap = async () => {
    const [config, reference] = await Promise.all([
      api.getPublicConfig(),
      api.fetchPublicReference()
    ]);
    setSystemConfig(config);
    setVenues(reference.venues || []);
    setServices(reference.services || []);
    setAddOns(reference.addOns || []);
    setUserGroups([]);
    setAppointments([]);
    setUsers([]);
    setCompanies([]);
    setCustomers([]);
    setQuestionnaires([]);
    setSubmissions([]);
    setCertificates([]);
    setPayments([]);
    setApiErrors({});
  };

  const loadStaffBootstrap = async () => {
    const [config, reference, userGroupsResult] = await Promise.all([
      api.getSystemConfig(),
      api.fetchPublicReference(),
      api.listStaffUserGroups({ page: 1, pageSize: 100 })
    ]);
    setSystemConfig(config);
    setVenues(reference.venues || []);
    setServices(reference.services || []);
    setAddOns(reference.addOns || []);
    setUserGroups(userGroupsResult.items || []);
    setAppointments([]);
    setUsers([]);
    setCompanies([]);
    setCustomers([]);
    setQuestionnaires([]);
    setSubmissions([]);
    setCertificates([]);
    setPayments([]);
    setApiErrors({});
  };

  const loadData = async (silent = false) => {
    if (silent) {
      silentLoadCountRef.current += 1;
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (session) {
        const data = await api.fetchInitialData();
        setVenues(data.venues || []);
        setServices(data.services || []);
        setAddOns(data.addOns || []);
        setAppointments(data.appointments || []);
        setUsers(data.users || []);
        setCompanies(data.companies || []);
        setCustomers(data.customers || []);
        setUserGroups(data.userGroups || []);
        setQuestionnaires(data.questionnaires || []);
        setSubmissions(data.submissions || []);
        setCertificates(data.certificates || []);
        setPayments(data.payments || []);
        setSystemConfig(data.systemConfig);
        setApiErrors(data.errors || {});
      } else {
        await loadPublicBootstrap();
      }

      await hydrateSession();
      setLoadError(null);
    } catch (e: any) {
      console.error('Critical Startup Error:', e);
      setLoadError('System connection failed. Check console for details.');
    } finally {
      if (silent) {
        silentLoadCountRef.current = Math.max(0, silentLoadCountRef.current - 1);
        if (silentLoadCountRef.current === 0) {
          setIsRefreshing(false);
        }
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData();
    openRecoveryModal();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowRecoveryModal(true);
      }
      if (event === 'SIGNED_OUT') {
        const suppress = localStorage.getItem('k2_suppress_signed_out');
        setCurrentSessionId(null);
        setCurrentUser(null);
        setCurrentCompany(null);
        setCurrentCustomer(null);
        setShowCustomerPasswordGate(false);
        setShowLoginModal(false);
        setView('home');
        if (suppress) {
          localStorage.removeItem('k2_suppress_signed_out');
          return;
        }
      } else if (event === 'SIGNED_IN' || session) {
        hydrateSession();
        loadData(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRecoveryPasswordUpdate = async () => {
    if (recoveryPassword !== recoveryConfirmPassword) {
      setRecoveryError('Passwords do not match.');
      return;
    }
    if (recoveryPassword.length < 6) {
      setRecoveryError('Password must be at least 6 characters.');
      return;
    }

    setIsUpdatingRecovery(true);
    setRecoveryError('');

    try {
      const sessionProfile = await api.getCurrentSession();
      if (sessionProfile?.type === 'company') {
        const memberId = sessionProfile.profile.loggedInMember?.id || 'primary';
        await api.updateCompanyPassword(sessionProfile.profile.id, memberId, recoveryPassword);
      } else if (sessionProfile?.type === 'customer') {
        await api.updateCustomerPassword(sessionProfile.profile.id, recoveryPassword);
      } else {
        const { error } = await supabase.auth.updateUser({ password: recoveryPassword });
        if (error) throw error;
      }

      await api.logout();
      clearRecoveryUrl();
      setShowRecoveryModal(false);
      setRecoveryPassword('');
      setRecoveryConfirmPassword('');
      showAlertModal('Password updated. Please sign in.');
      if (sessionProfile?.type) {
        setLoginContext(sessionProfile.type);
      }
      setShowLoginModal(true);
    } catch (e: any) {
      setRecoveryError(e?.message || 'Failed to update password.');
    } finally {
      setIsUpdatingRecovery(false);
    }
  };

  const handleCustomerPasswordUpdate = async () => {
    if (customerPassword !== customerConfirmPassword) {
      setCustomerPasswordError('Passwords do not match.');
      return;
    }
    if (customerPassword.length < 6) {
      setCustomerPasswordError('Password must be at least 6 characters.');
      return;
    }

    setIsUpdatingCustomerPassword(true);
    setCustomerPasswordError('');

    try {
      if (!currentCustomer) {
        throw new Error('Customer session not found.');
      }
      await api.updateCustomerPassword(currentCustomer.id, customerPassword);
      setShowCustomerPasswordGate(false);
      setCustomerPassword('');
      setCustomerConfirmPassword('');
      await hydrateSession();
      showAlertModal('Password updated. You can now use your portal.');
    } catch (e: any) {
      setCustomerPasswordError(e?.message || 'Failed to update password.');
    } finally {
      setIsUpdatingCustomerPassword(false);
    }
  };

  const handleRecoveryCancel = async () => {
    await api.logout();
    clearRecoveryUrl();
    setShowRecoveryModal(false);
    setRecoveryPassword('');
    setRecoveryConfirmPassword('');
    setRecoveryError('');
  };

  const handleLogout = async () => {
    await api.logout();
    setCurrentSessionId(null);
    setCurrentUser(null);
    setCurrentCompany(null);
    setCurrentCustomer(null);
    setShowCustomerPasswordGate(false);
    setCustomerPassword('');
    setCustomerConfirmPassword('');
    setCustomerPasswordError('');
    setView('home');
  };

  const handleCurrentSessionRevoked = async () => {
    setCurrentSessionId(null);
    setCurrentUser(null);
    setCurrentCompany(null);
    setCurrentCustomer(null);
    setShowCustomerPasswordGate(false);
    setShowLoginModal(false);
    setView('home');
    await supabase.auth.signOut();
  };

  const handleHeaderLogoClick = () => {
    setShowLoginModal(false);
    setShowRecoveryModal(false);
    setView('home');
  };

  const handleLoginSuccess = (u: User | null, c: Company | null, cust: Customer | null) => {
    if (u) {
      setCurrentUser(u);
      setCurrentCompany(null);
      setCurrentCustomer(null);
    }
    if (c) {
      setCurrentCompany(c);
      setCurrentUser(null);
      setCurrentCustomer(null);
    }
    if (cust) {
      setCurrentCustomer(cust);
      setCurrentUser(null);
      setCurrentCompany(null);
    }
    setShowLoginModal(false);
    loadData(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="bg-white p-8 rounded-3xl shadow-xl flex flex-col items-center border border-slate-100">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mb-4" />
          <p className="font-black text-slate-800 uppercase tracking-widest text-xs">
            Connecting to Database...
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Supabase Connection Error</h2>
          <p className="text-slate-500 mb-8">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (isVerifyMode) return <VerificationPage systemConfig={systemConfig} />;

  return (
    <Layout
      currentUser={currentUser}
      currentCompany={currentCompany}
      onLogout={handleLogout}
      onLoginClick={() => {
        setLoginContext('staff');
        setShowLoginModal(true);
      }}
      onLogoClick={handleHeaderLogoClick}
      appName={systemConfig?.general?.appName}
      logoUrl={systemConfig?.general?.logoUrl}
    >
      {Object.keys(apiErrors).length > 0 && currentUser && (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-center gap-4 text-xs font-black uppercase tracking-widest sticky top-16 z-[45]">
          <ShieldAlert className="w-4 h-4" />
          Database Sync Errors Detected. Check Admin {'>'} Settings for Diagnostics.
          <button onClick={() => setApiErrors({})} className="ml-4 opacity-50 hover:opacity-100">
            Dismiss
          </button>
        </div>
      )}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        context={loginContext}
        users={users}
        companies={companies}
        customers={customers}
        onLoginSuccess={handleLoginSuccess}
      />
      {showCustomerPasswordGate && currentCustomer && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Set Your Password</h3>
            <p className="text-xs text-slate-500 mb-4">
              Please set a password to finish activating your patient portal access.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                  value={customerPassword}
                  onChange={(e) => setCustomerPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                  value={customerConfirmPassword}
                  onChange={(e) => setCustomerConfirmPassword(e.target.value)}
                />
              </div>
              {customerPasswordError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {customerPasswordError}
                </p>
              )}
              <button
                onClick={handleCustomerPasswordUpdate}
                disabled={isUpdatingCustomerPassword}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUpdatingCustomerPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                  </>
                ) : (
                  'Save Password'
                )}
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-slate-500 text-xs font-bold hover:text-slate-700"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
      {showRecoveryModal && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full relative border border-slate-200">
            <h3 className="font-bold text-lg text-slate-800 mb-1">Reset Password</h3>
            <p className="text-xs text-slate-500 mb-4">
              Set a new password to continue to your portal.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                  value={recoveryPassword}
                  onChange={(e) => setRecoveryPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                  value={recoveryConfirmPassword}
                  onChange={(e) => setRecoveryConfirmPassword(e.target.value)}
                />
              </div>
              {recoveryError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {recoveryError}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleRecoveryCancel}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded"
                  disabled={isUpdatingRecovery}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecoveryPasswordUpdate}
                  disabled={isUpdatingRecovery}
                  className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isUpdatingRecovery ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentUser && currentUser.role !== 'technician' && (
        <AdminDashboard
          isRefreshing={isRefreshing}
          currentUser={currentUser}
          currentSessionId={currentSessionId}
          services={services}
          venues={venues}
          addOns={addOns}
          userGroups={userGroups}
          companies={companies}
          customers={customers}
          questionnaires={questionnaires}
          submissions={submissions}
          systemConfig={systemConfig}
          onUpdateSystemConfig={(c) => api.updateSystemConfig(c).then(() => loadData(true))}
          onUpdateService={(s) => api.updateService(s).then(() => loadData(true))}
          onAddService={(s) => api.createService(s).then(() => loadData(true))}
          onDeleteService={(id) => api.deleteService(id).then(() => loadData(true))}
          onUpdateVenue={(v) => api.updateVenue(v).then(() => loadData(true))}
          onAddVenue={(v) => api.createVenue(v).then(() => loadData(true))}
          onDeleteVenue={(id) => api.deleteVenue(id).then(() => loadData(true))}
          onUpdateAddOn={(a) => api.updateAddOn(a).then(() => loadData(true))}
          onAddAddOn={(a) => api.createAddOn(a).then(() => loadData(true))}
          onDeleteAddOn={(id) => api.deleteAddOn(id).then(() => loadData(true))}
          onAddUser={(u) => api.createStaffUser(u).then(() => loadData(true))}
          onUpdateUser={(u) => api.updateUserProfile(u).then(() => loadData(true))}
          onDeleteUser={async (id) => {
            await api.deleteUser(id);
            await loadData(true);
          }}
          onUpdateCustomer={(c) => api.updateCustomer(c).then(() => loadData(true))}
          onResetPassword={async (id) => {
            await api.resetStaffPassword(id);
            loadData(true);
          }}
          onCurrentSessionRevoked={handleCurrentSessionRevoked}
          onAddUserGroup={(g) => api.createUserGroup(g).then(() => loadData(true))}
          onUpdateUserGroup={(g) => api.updateUserGroup(g).then(() => loadData(true))}
          onDeleteUserGroup={(id) => api.deleteUserGroup(id).then(() => loadData(true))}
          onApproveCompany={(id, tier, pref, po) =>
            api
              .updateCompany({
                id,
                status: 'active',
                tier,
                paymentPreference: pref,
                requirePo: po
              })
              .then(() => loadData(true))
          }
          onRejectCompany={(id) =>
            api.updateCompany({ id, status: 'rejected' }).then(() => loadData(true))
          }
          onUpdateCompany={(c) => api.updateCompany(c).then(() => loadData(true))}
          onDeleteCompany={async (id) => {
            await api.deleteCompany(id);
            await loadData(true);
          }}
          onAddCompany={(c) => api.createCompanyWithAuth(c).then(() => loadData(true))}
          onSendReminder={async (appointmentId, type, templateType) => {
            try {
              const response = await api.triggerReminder(appointmentId, type, templateType);
              const emailSent = response?.result?.emailSent || 0;
              const smsSent = response?.result?.smsSent || 0;

              if (emailSent > 0 || smsSent > 0) {
                const channels = [
                  emailSent > 0 ? `email (${emailSent})` : null,
                  smsSent > 0 ? `sms (${smsSent})` : null
                ]
                  .filter(Boolean)
                  .join(' and ');
                showAlertModal(`Notification sent via ${channels}.`);
              } else {
                const firstError = response?.result?.errors?.[0];
                showAlertModal(
                  firstError ||
                    'No notifications were sent. Check recipient details and channel setup.'
                );
              }

              await loadData(true);
            } catch (err: any) {
              showAlertModal(err?.message || 'Failed to send notification.');
            }
          }}
          onUpdateBooking={(b, notify) => {
            return api.updateBooking(b, notify).then(() => loadData(true));
          }}
          onAddBooking={(b) => api.createBooking(b).then(() => loadData(true))}
          onDeleteBooking={(id) => api.deleteBooking(id).then(() => loadData(true))}
          onForceRefresh={() => loadData(true)}
          onAddQuestionnaire={(q) => api.createQuestionnaire(q).then(() => loadData(true))}
          onUpdateQuestionnaire={(q) => api.updateQuestionnaire(q).then(() => loadData(true))}
          onDeleteQuestionnaire={(id) => api.deleteQuestionnaire(id).then(() => loadData(true))}
        />
      )}

      {currentUser && currentUser.role === 'technician' && (
        <TechnicianView
          currentUser={currentUser}
          appointments={appointments}
          services={services}
          venues={venues}
          questionnaires={questionnaires}
          submissions={submissions}
          onUpdateStatus={async (id, status) => {
            await api.updateBookingStatus(id, status);
            await loadData(true);
          }}
          onUpdateUser={(u) => {
            setCurrentUser(u);
            loadData(true);
          }}
          onIssueCertificate={(c) =>
            api.issueCertificate(c).then(() => {
              loadData(true);
              return { success: true };
            })
          }
        />
      )}

      {currentCompany && (
        <CompanyDashboard
          isRefreshing={isRefreshing}
          company={currentCompany}
          appointments={appointments}
          services={services}
          venues={venues}
          addOns={addOns}
          certificates={certificates}
          systemConfig={systemConfig}
          termsAndConditions={systemConfig?.termsAndConditions || ''}
          onBookNew={async (app) => {
            await api.createBooking(app);
            await loadData(true);
          }}
          onUpdateCompany={(c) => api.updateCompany(c).then(() => loadData(true))}
          onForceRefresh={() => loadData(true)}
        />
      )}

      {currentCustomer && (
        <CustomerPortal
          customer={currentCustomer}
          appointments={appointments}
          services={services}
          venues={venues}
          addOns={addOns}
          questionnaires={questionnaires}
          submissions={submissions}
          certificates={certificates}
          systemConfig={systemConfig}
          onLogout={handleLogout}
          onSubmitQuestionnaire={(s) => api.submitQuestionnaire(s).then(() => loadData(true))}
          onUpdateBooking={(b) => api.updateBooking(b).then(() => loadData(true))}
        />
      )}

      {!currentUser &&
        !currentCompany &&
        !currentCustomer &&
        (view === 'booking' ? (
          <BookingFlow
            venues={venues}
            services={services}
            addOns={addOns}
            appointments={appointments}
            termsAndConditions={systemConfig?.termsAndConditions || ''}
            onComplete={async (app) => {
              await api.createBooking(app);
              setView('home');
              await loadData(true);
            }}
            onBack={() => setView('home')}
          />
        ) : (
          <LandingPage
            systemConfig={systemConfig}
            onNavigateBooking={() => setView('booking')}
            onLoginClick={(ctx) => {
              setLoginContext(ctx);
              setShowLoginModal(true);
            }}
          />
        ))}
      <AlertModalHost />
      <ToastHost />
    </Layout>
  );
}
